import { useParams } from 'react-router';
import { useArticleBySlug } from '@data/articles/useArticleBySlug';
import { ArticlePreview } from '@ui/article/ArticlePreview';
import { SocialNetworks } from '@ui/league/SocialNetworks';
import { ArticleOrder, ArticleVariant } from '@repo/types';
import { ArticleList } from '@ui/article/ArticleList';
import { DeviceType } from '@repo/types';
import NewsletterSubscribe from '@ui/article/NewsletterSubscribe/NewsletterSubscribe';
import { SponsorsGrid } from '@ui/league/SponsorsGrid';
import { Helmet } from 'react-helmet-async';
import { useIsMobile } from '../../utils/useIsMobile';
import { useDeviceType } from '@ui/utils/useDeviceType';
import { NotFoundPage } from '../NotFoundPage';
import { Loader } from '@ui/common/components/Loader/Loader';

export function ArticlePage() {
  const params = useParams();
  const deviceType = useDeviceType();
  const isMobile = useIsMobile();
  const { data: article, isLoading, isError } = useArticleBySlug(params.slug);

  const articleImage = article?.imageSrc?.startsWith('http')
    ? article.imageSrc
    : `${import.meta.env.VITE_ASSETS_DOMAIN}/${article?.imageSrc}`;

  if (isLoading) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: 'calc(100vh - 100px)',
        }}
      >
        <Loader />
      </div>
    );
  }

  if (!params.slug || isError || !article) {
    return <NotFoundPage />;
  }

  return (
    <>
      <Helmet prioritizeSeoTags>
        <title>{article.title}</title>
        <meta name="description" content={article.summary} />
        {article.author && <meta name="author" content={article.author} />}
        <meta property="og:title" content={article.title} />
        <meta property="og:description" content={article.summary} />
        <meta property="og:image" content={articleImage} />
        <meta property="og:image:alt" content={article.title} />
        <meta property="og:type" content="article" />
        <meta property="og:tag" content={article.tags?.toString()} />
        <meta name="twitter:title" content={article.title} />
        <meta name="twitter:description" content={article.summary} />
        <meta name="twitter:image" content={articleImage} />
      </Helmet>
      <div className="article-details-page">
        <div className="article-details-page__article-preview-panel">
          <ArticlePreview article={article}>
            <SocialNetworks />
          </ArticlePreview>
        </div>
        <div className="article-details-page__article-list-panel">
          <ArticleList
            title="Related articles"
            page={1}
            limit={4}
            variant={ArticleVariant.Default}
            tags={article.tags[0]}
            withPagination={false}
            showAuthor={false}
            showSummary={false}
            showDate={false}
            showImage
            showTag
            columns={deviceType === DeviceType.mobile ? 2 : 4}
            order={ArticleOrder.Desc}
            startDate="2023-07-01"
            excludeIds={article.id}
          />
        </div>
        <NewsletterSubscribe title={'Get the latest news straight to your inbox'} />
        <div className="article-details-page__sponsors-grid-panel">
          <SponsorsGrid
            className="matches-list__sponsors-grid"
            title="Our Trusted Partners"
            showAllSponsors
            showImage
            columns={isMobile ? 3 : 5}
            showName={false}
            showDescription={false}
          />
        </div>
      </div>
    </>
  );
}
